import { useState, useRef, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useTournament } from '../context/TournamentContext';
import { MatchUp } from '../data/types';
import { StatusBadge } from './LiveIndicator';
import { calculateGameResult, buildScoreMaps, calculateMatchPlay } from '../engine/scoring';

interface MatchCardProps {
  matchUp: MatchUp;
}

export function MatchCard({ matchUp }: MatchCardProps) {
  const navigate = useNavigate();
  const { getTeam, getGamesByMatchUp, getScoresByGame, getPlayer, matchUpResults } = useTournament();
  const [expandedGames, setExpandedGames] = useState<Set<string>>(new Set());

  const topScrollRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const bottomScrollRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});

  const handleScroll = (e: React.UIEvent<HTMLDivElement>, gameId: string, source: 'top' | 'bottom') => {
    const target = e.target as HTMLDivElement;
    const scrollLeft = target.scrollLeft;
    if (source === 'top') {
      const bottom = bottomScrollRefs.current[gameId];
      if (bottom && bottom.scrollLeft !== scrollLeft) bottom.scrollLeft = scrollLeft;
    } else {
      const top = topScrollRefs.current[gameId];
      if (top && top.scrollLeft !== scrollLeft) top.scrollLeft = scrollLeft;
    }
  };

  const teamA = matchUp.team_a_id ? getTeam(matchUp.team_a_id) : null;
  const teamB = matchUp.team_b_id ? getTeam(matchUp.team_b_id) : null;
  const games = getGamesByMatchUp(matchUp.id);
  const result = matchUpResults.get(matchUp.id);

  const hasLiveGames = games.some(g => g.status === 'in_progress');
  const displayStatus = hasLiveGames ? 'in_progress' : matchUp.status;

  const location = useLocation();
  const lastProcessedHash = useRef<string | null>(null);
  const gamesRef = useRef(games);
  gamesRef.current = games;

  useEffect(() => {
    if (location.hash && gamesRef.current.length > 0 && location.hash !== lastProcessedHash.current) {
      const targetGameId = location.hash.replace('#game-', '');
      const hasGame = gamesRef.current.some(g => g.id === targetGameId);
      
      if (hasGame) {
        lastProcessedHash.current = location.hash;
        setExpandedGames(prev => {
          const next = new Set(prev);
          next.add(targetGameId);
          return next;
        });
        
        setTimeout(() => {
          const element = document.getElementById(`game-${targetGameId}`);
          if (element) {
            const y = element.getBoundingClientRect().top + window.scrollY - 80;
            window.scrollTo({ top: y, behavior: 'smooth' });
          }
        }, 300);
      }
    }
  }, [location.hash]);

  // Helper: get player initials
  function getInitials(name?: string): string {
    if (!name) return '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  }

  // Helper: get player names for a game side
  function getSidePlayers(game: typeof games[0], side: 'a' | 'b') {
    if (game.type === 'single') {
      const pid = side === 'a' ? game.player_a_id : game.player_b_id;
      return pid ? [getPlayer(pid)] : [];
    }
    const p1id = side === 'a' ? game.foursome_a1_id : game.foursome_b1_id;
    const p2id = side === 'a' ? game.foursome_a2_id : game.foursome_b2_id;
    return [p1id ? getPlayer(p1id) : undefined, p2id ? getPlayer(p2id) : undefined].filter(Boolean);
  }

  if (games.length === 0) {
    // Fallback: simple card if no games yet
    return (
      <div
        className="ryder-card"
        onClick={() => navigate(`/match/${matchUp.id}`)}
        id={`match-card-${matchUp.id}`}
        style={{ cursor: 'pointer' }}
      >
        <div className="ryder-header tied">
          <div className="ryder-team-left">
            <div className="ryder-names">
              <span className="ryder-player-name">{teamA?.name ?? 'TBD'}</span>
            </div>
          </div>
          <div className="ryder-center">
            <div className="ryder-match-title">{matchUp.label}</div>
            <div className="ryder-score-val">—</div>
            <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '4px', textTransform: 'uppercase' as const }}>
              {matchUp.scheduled_time ?? 'Nezačal'}
            </div>
          </div>
          <div className="ryder-team-right ryder-team-right-desktop">
            <div className="ryder-names">
              <span className="ryder-player-name">{teamB?.name ?? 'TBD'}</span>
            </div>
          </div>
        </div>
        {/* Mobile team B */}
        <div className="ryder-team-b-mobile">
          <div className="ryder-header ryder-header-b-mobile tied">
            <div className="ryder-team-right" style={{ flex: 1, justifyContent: 'flex-end' }}>
              <div className="ryder-names">
                <span className="ryder-player-name">{teamB?.name ?? 'TBD'}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div id={`match-card-${matchUp.id}`}>
      {games.map((game, gameIdx) => {
        const scores = getScoresByGame(game.id);
        const gameResult = scores.length > 0 ? calculateGameResult(game, scores) : null;
        const { sideA, sideB } = buildScoreMaps(scores);
        const mpState = gameResult?.matchPlayState ?? null;

        // Determine state class
        let stateClass = 'tied';
        let scoreText = '—';

        if (mpState && mpState.holesPlayed > 0) {
          if (mpState.isFinished) {
            if (mpState.winnerSide === 'a') {
              stateClass = 'a-won';
              scoreText = mpState.resultText ?? 'Halved';
            } else if (mpState.winnerSide === 'b') {
              stateClass = 'b-won';
              scoreText = mpState.resultText ?? 'Halved';
            } else {
              stateClass = 'tied';
              scoreText = 'Halved';
            }
          } else {
            if (mpState.leaderSide === 'a') {
              stateClass = 'a-won';
              scoreText = `${mpState.lead} UP`;
            } else if (mpState.leaderSide === 'b') {
              stateClass = 'b-won';
              scoreText = `${mpState.lead} UP`;
            } else {
              stateClass = 'tied';
              scoreText = 'A/S';
            }
          }
        }

        const sideAPlayers = getSidePlayers(game, 'a');
        const sideBPlayers = getSidePlayers(game, 'b');

        const gameLabel = game.type === 'foursome'
          ? 'Foursome'
          : `Single ${game.game_number}`;

        const statusLabel = mpState?.isFinished
          ? 'Konec'
          : mpState && mpState.holesPlayed > 0
          ? `Jamka ${mpState.holesPlayed}`
          : game.tee_time
          ? `⏱ ${game.tee_time}`
          : matchUp.scheduled_time ?? 'Nezačal';


        return (
          <div key={game.id} id={`game-${game.id}`} style={{ marginBottom: gameIdx < games.length - 1 ? '12px' : 0 }}>
            <div className="ryder-card">
              {/* Header with teams */}
              <div
                className={`ryder-header ${stateClass}`}
                onClick={() => navigate(`/match/${matchUp.id}`)}
                style={{ cursor: 'pointer' }}
              >
                {/* Team A (left) */}
                <div className="ryder-team-left">
                  <div className="ryder-avatars">
                    {sideAPlayers.map((p, i) => (
                      <div key={i} className="ryder-avatar" style={{
                        background: p?.avatar_url
                          ? `url(${p.avatar_url}) center/cover`
                          : 'var(--bg-surface)',
                        color: p?.avatar_url ? 'transparent' : 'inherit',
                      }}>
                        {!p?.avatar_url && getInitials(p?.name)}
                      </div>
                    ))}
                  </div>
                  <div className="ryder-names">
                    {sideAPlayers.map((p, i) => (
                      <span key={i} className="ryder-player-name">{p?.name ?? 'TBD'}</span>
                    ))}
                  </div>
                </div>

                {/* Center score */}
                <div className="ryder-center">
                  <div className="ryder-match-title">
                    {gameLabel}
                  </div>
                  <div className="ryder-score-val">
                    {scoreText}
                  </div>
                  <div style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: '4px', textTransform: 'uppercase' as const }}>
                    {statusLabel}
                  </div>
                </div>

                {/* Team B (right) — desktop only */}
                <div className="ryder-team-right ryder-team-right-desktop">
                  <div className="ryder-names">
                    {sideBPlayers.map((p, i) => (
                      <span key={i} className="ryder-player-name">{p?.name ?? 'TBD'}</span>
                    ))}
                  </div>
                  <div className="ryder-avatars">
                    {sideBPlayers.map((p, i) => (
                      <div key={i} className="ryder-avatar" style={{
                        background: p?.avatar_url
                          ? `url(${p.avatar_url}) center/cover`
                          : 'var(--bg-surface)',
                        color: p?.avatar_url ? 'transparent' : 'inherit',
                      }}>
                        {!p?.avatar_url && getInitials(p?.name)}
                      </div>
                    ))}
                  </div>
                </div>
              </div>

              {/* Holes row */}
              <div className="ryder-holes-container">
                <div
                  className="ryder-holes-row"
                  ref={el => { topScrollRefs.current[game.id] = el; }}
                  onScroll={(e) => handleScroll(e, game.id, 'top')}
                  style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch' }}
                >
                  {Array.from({ length: 18 }, (_, i) => {
                    const holeNum = i + 1;
                    const aStrokes = sideA.get(holeNum);
                    const bStrokes = sideB.get(holeNum);
                    let circleClass = '';
                    if (aStrokes !== undefined && bStrokes !== undefined) {
                      if (aStrokes < bStrokes) circleClass = 'won-a';
                      else if (bStrokes < aStrokes) circleClass = 'won-b';
                      else circleClass = 'tied';
                    }
                    return (
                      <div key={holeNum} className="ryder-hole-col">
                        <div className={`ryder-hole-circle ${circleClass}`}>{holeNum}</div>
                      </div>
                    );
                  })}
                </div>
              </div>

              {/* Chevron toggle for expandable scorecard */}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setExpandedGames(prev => {
                    const next = new Set(prev);
                    if (next.has(game.id)) next.delete(game.id);
                    else next.add(game.id);
                    return next;
                  });
                }}
                style={{
                  width: '100%', background: 'none', border: 'none',
                  borderTop: '1px solid var(--border)', cursor: 'pointer',
                  padding: '6px 0', display: 'flex', alignItems: 'center',
                  justifyContent: 'center', color: 'var(--text-muted)',
                  transition: 'color 0.2s',
                }}
                aria-label="Zobrazit/skrýt kumulativní skóre"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  style={{
                    transform: expandedGames.has(game.id) ? 'rotate(180deg)' : 'rotate(0deg)',
                    transition: 'transform 0.4s cubic-bezier(0.4,0,0.2,1)',
                  }}
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {/* Animated cumulative scorecard */}
              <div style={{
                maxHeight: expandedGames.has(game.id) ? '600px' : '0',
                opacity: expandedGames.has(game.id) ? 1 : 0,
                overflow: 'hidden',
                transition: expandedGames.has(game.id)
                  ? 'max-height 0.6s cubic-bezier(0.4,0,0.2,1), opacity 0.45s ease'
                  : 'max-height 0.45s cubic-bezier(0.4,0,0.2,1), opacity 0.3s ease',
              }}>
                <div
                  ref={el => { bottomScrollRefs.current[game.id] = el; }}
                  onScroll={(e) => handleScroll(e, game.id, 'bottom')}
                  style={{ overflowX: 'auto', maxWidth: '100%' }}
                >
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.75rem' }}>
                    <thead>
                      <tr>
                        {Array.from({ length: 18 }, (_, i) => (
                          <th key={i + 1} style={{
                            padding: '6px 6px', textAlign: 'center', fontWeight: 600,
                            color: 'var(--text-muted)', background: 'var(--bg-surface)', minWidth: 30,
                          }}>{i + 1}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      <tr style={{ borderBottom: '1px solid var(--border)' }}>
                        {(() => {
                          let runningA = 0;
                          let runningB = 0;
                          return Array.from({ length: 18 }, (_, i) => {
                            const holeNum = i + 1;
                            const aS = sideA.get(holeNum);
                            const bS = sideB.get(holeNum);
                            if (aS === undefined || bS === undefined) {
                              return <td key={holeNum} style={{
                                padding: '6px 3px', textAlign: 'center',
                                color: 'var(--text-muted)', fontSize: '0.65rem', fontWeight: 600,
                              }}>—</td>;
                            }
                            if (aS < bS) runningA++;
                            else if (bS < aS) runningB++;
                            const diff = runningA - runningB;
                            let label = 'AS';
                            let bg = 'transparent';
                            let color = 'var(--text-muted)';
                            if (diff > 0) { label = `${diff}UP`; bg = 'var(--side-a)'; color = '#fff'; }
                            else if (diff < 0) { label = `${Math.abs(diff)}UP`; bg = 'var(--side-b)'; color = '#fff'; }
                            return (
                              <td key={holeNum} style={{
                                padding: '6px 3px', textAlign: 'center',
                                background: bg, color, fontWeight: 700,
                                fontSize: '0.65rem', fontFamily: 'var(--font-mono)',
                                whiteSpace: 'nowrap', transition: 'background 0.2s',
                              }}>{label}</td>
                            );
                          });
                        })()}
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Team B — mobile only */}
              <div
                className="ryder-team-b-mobile"
                onClick={() => navigate(`/match/${matchUp.id}`)}
                style={{ cursor: 'pointer' }}
              >
                <div className={`ryder-header ryder-header-b-mobile ${stateClass}`}>
                  <div className="ryder-team-right" style={{ flex: 1, justifyContent: 'flex-end' }}>
                    <div className="ryder-names">
                      {sideBPlayers.map((p, i) => (
                        <span key={i} className="ryder-player-name">{p?.name ?? 'TBD'}</span>
                      ))}
                    </div>
                    <div className="ryder-avatars">
                      {sideBPlayers.map((p, i) => (
                        <div key={i} className="ryder-avatar" style={{
                          background: p?.avatar_url
                            ? `url(${p.avatar_url}) center/cover`
                            : 'var(--bg-surface)',
                          color: p?.avatar_url ? 'transparent' : 'inherit',
                        }}>
                          {!p?.avatar_url && getInitials(p?.name)}
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}
